import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, X } from "lucide-react";
import { instructorApi } from "../api/instructor";
import type { LearningBooklet, LearningProgression } from "../types";
import { useUiStore } from "../store/ui";
import { fmtDate } from "../lib/dates";

const LEVEL_LABEL: Record<string, { label: string; tone: string }> = {
  acquis: {
    label: "Acquis",
    tone: "bg-green-50 text-green-700 border-green-200",
  },
  en_cours: {
    label: "En cours",
    tone: "bg-amber-50 text-amber-700 border-amber-200",
  },
  a_revoir: {
    label: "À revoir",
    tone: "bg-red-50 text-red-700 border-red-200",
  },
};

// Progression renseignée par le moniteur (éditable).
const PROGRESSION_STEPS: { value: LearningProgression; label: string }[] = [
  { value: "aborde", label: "Abordé" },
  { value: "traite", label: "Traité" },
  { value: "assimile", label: "Assimilé" },
];
const PROGRESSION_RANK: Record<string, number> = {
  aborde: 1,
  traite: 2,
  assimile: 3,
};

// Auto-évaluation de l'élève.
const SELF_ICON: Record<number, string> = { 1: "🙁", 2: "😐", 3: "🙂" };
const SELF_LABEL: Record<number, string> = {
  1: "Peu sûr",
  2: "À confirmer",
  3: "À l'aise",
};
const SELF_TONE: Record<number, string> = {
  1: "text-red-500",
  2: "text-amber-500",
  3: "text-green-600",
};

function ReferentialBooklet({ studentId }: { studentId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["student", studentId, "learning-booklet"],
    queryFn: () => instructorApi.getStudentLearningBooklet(studentId),
    enabled: !!studentId,
  });

  const mutation = useMutation({
    mutationFn: (vars: {
      itemCode: string;
      progression: LearningProgression | null;
    }) =>
      instructorApi.setStudentLearningProgression(
        studentId,
        vars.itemCode,
        vars.progression,
      ),
    onSuccess: (fresh: LearningBooklet) => {
      queryClient.setQueryData(
        ["student", studentId, "learning-booklet"],
        fresh,
      );
    },
  });

  if (isLoading)
    return <p className="text-slate-500 text-sm">Chargement du référentiel…</p>;
  if (isError || !data) return null;

  return (
    <div className="rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-indigo-50 border-b border-indigo-100">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">
            Référentiel de compétences
          </h3>
          <p className="text-xs text-slate-500 truncate">
            {data.referentialLabel} · renseignez la progression de l'élève
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-xl font-extrabold text-indigo-600 leading-none">
            {data.overallPct}%
          </div>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {data.categories.map((cat) => (
          <div key={cat.code} className="px-4 py-3">
            <div className="flex items-center justify-between gap-3 mb-2">
              <h4 className="text-xs font-semibold text-slate-800 flex items-baseline gap-1.5 min-w-0">
                <span className="text-indigo-600 font-extrabold">
                  {cat.code}
                </span>
                <span className="truncate">{cat.title}</span>
              </h4>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="w-16 h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 rounded-full"
                    style={{ width: `${cat.progressPct}%` }}
                  />
                </div>
                <span className="text-xs font-bold text-indigo-600 w-8 text-right">
                  {cat.progressPct}%
                </span>
              </div>
            </div>
            <ul className="space-y-2">
              {cat.items.map((item) => {
                const rank = item.progression
                  ? PROGRESSION_RANK[item.progression]
                  : 0;
                return (
                  <li
                    key={item.code}
                    className="flex flex-wrap items-center justify-between gap-2 text-sm"
                  >
                    <span className="text-slate-600 flex-1 min-w-[160px]">
                      {item.label}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="inline-flex rounded-lg overflow-hidden border border-slate-200">
                        {PROGRESSION_STEPS.map((step) => {
                          const active = rank >= PROGRESSION_RANK[step.value];
                          return (
                            <button
                              key={step.value}
                              type="button"
                              disabled={mutation.isPending}
                              title={step.label}
                              onClick={() =>
                                mutation.mutate({
                                  itemCode: item.code,
                                  // Re-cliquer le niveau courant le retire.
                                  progression:
                                    item.progression === step.value
                                      ? null
                                      : step.value,
                                })
                              }
                              className={`px-2.5 py-1 text-[11px] font-semibold border-l first:border-l-0 border-slate-200 transition disabled:opacity-60 ${
                                active
                                  ? "bg-indigo-600 text-white"
                                  : "bg-white text-slate-500 hover:bg-indigo-50"
                              }`}
                            >
                              {step.label}
                            </button>
                          );
                        })}
                      </div>
                      <span
                        className={`text-base w-5 text-center ${
                          item.selfAssessment
                            ? SELF_TONE[item.selfAssessment]
                            : "text-slate-300"
                        }`}
                        title={
                          item.selfAssessment
                            ? `Auto-évaluation élève : ${SELF_LABEL[item.selfAssessment]}`
                            : "Pas d'auto-évaluation"
                        }
                      >
                        {item.selfAssessment
                          ? SELF_ICON[item.selfAssessment]
                          : "–"}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LivretModal() {
  const studentId = useUiStore((s) => s.livretStudentId);
  const close = useUiStore((s) => s.closeLivret);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["student", studentId],
    queryFn: () => instructorApi.getStudent(studentId as string),
    enabled: !!studentId,
  });

  if (!studentId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto animate-fade-in">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <h2 className="text-slate-900 text-lg font-semibold">
              Livret d'apprentissage
            </h2>
            {data && (
              <p className="text-xs text-slate-500">
                {data.firstName} {data.lastName} · {data.drivingHoursDone}/
                {data.drivingHoursTarget} h de conduite
              </p>
            )}
          </div>
          <button
            onClick={close}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {isLoading && <p className="text-slate-500">Chargement du livret…</p>}
          {isError && (
            <p className="text-red-600">
              Impossible de charger le livret : {(error as Error).message}
            </p>
          )}

          {studentId && <ReferentialBooklet studentId={studentId} />}

          {data && data.bilans.length > 0 && (
            <h3 className="text-sm font-semibold text-slate-900 pt-1">
              Bilans de leçon
            </h3>
          )}

          {data && data.bilans.length === 0 && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-500 text-sm">
              Aucun bilan enregistré pour cet élève.
            </div>
          )}

          {data?.bilans.map((b) => (
            <div
              key={b.id}
              className="rounded-2xl border border-slate-200 p-4 shadow-sm"
            >
              {b.nextObjective && (
                <p className="p-3 rounded-lg bg-indigo-50 text-indigo-900 text-sm">
                  <strong>Prochain cours :</strong> {b.nextObjective}
                </p>
              )}
              {b.examReason && (
                <p className="text-sm text-slate-600">
                  Avis examen : {b.examReason}
                </p>
              )}
              {b.secretaryAlert && (
                <p className="text-sm text-amber-700">
                  Secrétariat : {b.secretaryAlert}
                </p>
              )}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-900">
                  {fmtDate(b.lessonDate ?? b.createdAt)}
                </span>
                <div className="flex items-center gap-2">
                  {b.readyForExam && (
                    <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 text-[11px] font-semibold">
                      Prêt examen
                    </span>
                  )}
                  {b.rating != null && (
                    <span className="flex items-center gap-0.5 text-amber-500 text-xs">
                      <Star
                        size={13}
                        className="fill-amber-400 text-amber-400"
                      />
                      {b.rating}/5
                    </span>
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-600 whitespace-pre-line">
                {b.summary}
              </p>
              {b.competencies.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {b.competencies.map((c, i) => {
                    const meta = LEVEL_LABEL[c.level] ?? {
                      label: c.level,
                      tone: "bg-slate-50 text-slate-600 border-slate-200",
                    };
                    return (
                      <span
                        key={`${c.label}-${i}`}
                        className={`px-2.5 py-1 rounded-full border text-[11px] font-medium ${meta.tone}`}
                      >
                        {c.label} · {meta.label}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
